import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductSalesByLocationMachine } from '../../services/openapi';
import { buildProductSalesReport, formatAmount, formatQuantity } from './productSalesGrouping';

type ProductSalesReportProps = {
    rows: ProductSalesByLocationMachine[];
};

// The same fixed widths in both tables, so their quantity and amount columns line up.
const SalesReportColumns = () => (
    <colgroup>
        <col />
        <col className="sales-number-col" />
        <col className="sales-number-col" />
    </colgroup>
);

const ProductSalesReport = ({ rows }: ProductSalesReportProps) => {
    const { t } = useTranslation();
    const unknownLabel = t('productSales.unknown');

    const { locationGroups, productTotals, grandTotalQuantity, grandTotalAmountInCents, showSiteId } = useMemo(() => buildProductSalesReport(rows, unknownLabel), [rows, unknownLabel]);

    if (rows.length === 0) {
        return <div className="p-3">{t('productSales.noData')}</div>;
    }

    return (
        <div className="table-responsive">
            <table className="table sales-report-table qvend-table-font mb-4">
                <SalesReportColumns />
                <thead>
                    <tr className="sales-header-row">
                        <th>{t('productSales.product')}</th>
                        <th className="align-right">{t('productSales.quantity')}</th>
                        <th className="align-right">{t('productSales.amount')}</th>
                    </tr>
                </thead>
                <tbody>
                    {locationGroups.map((location, index) => (
                        <React.Fragment key={location.locationKey}>
                            {showSiteId && index > 0 && location.siteId !== locationGroups[index - 1].siteId && (
                                <tr className="sales-site-spacer-row" aria-hidden="true">
                                    <td colSpan={3}></td>
                                </tr>
                            )}
                            <tr className="sales-location-row">
                                <td colSpan={3}>
                                    {location.locationDescription}
                                    {showSiteId && <span className="sales-site-label">{t('productSales.site')}: {location.siteLabel}</span>}
                                </td>
                            </tr>
                            {location.machines.map((machine) => (
                                <React.Fragment key={machine.machineKey}>
                                    <tr className="sales-machine-row">
                                        <td colSpan={3}>{machine.machineDescription}</td>
                                    </tr>
                                    {machine.rows.map((row, index) => (
                                        <tr key={`${machine.machineKey}-${row.productId ?? row.productDescription ?? index}`} className="sales-product-row">
                                            <td>{row.productDescription ?? unknownLabel}</td>
                                            <td className="align-right">{formatQuantity(row.totalQuantity)}</td>
                                            <td className="align-right">{formatAmount(row.totalAmountInCents)}</td>
                                        </tr>
                                    ))}
                                    <tr className="sales-subtotal-row">
                                        <td>{t('productSales.subtotal')} {machine.machineDescription}</td>
                                        <td className="align-right">{formatQuantity(machine.totalQuantity)}</td>
                                        <td className="align-right">{formatAmount(machine.totalAmountInCents)}</td>
                                    </tr>
                                </React.Fragment>
                            ))}
                            <tr className="sales-location-total-row">
                                <td>{t('productSales.total')} {location.locationDescription}</td>
                                <td className="align-right">{formatQuantity(location.totalQuantity)}</td>
                                <td className="align-right">{formatAmount(location.totalAmountInCents)}</td>
                            </tr>
                        </React.Fragment>
                    ))}
                </tbody>
            </table>

            <table className="table sales-report-table qvend-table-font">
                <SalesReportColumns />
                <thead>
                    <tr className="sales-header-row">
                        <th colSpan={3}>{t('productSales.totalsPerProduct')}</th>
                    </tr>
                    <tr className="sales-subheader-row">
                        <th>{t('productSales.product')}</th>
                        <th className="align-right">{t('productSales.quantity')}</th>
                        <th className="align-right">{t('productSales.amount')}</th>
                    </tr>
                </thead>
                <tbody>
                    {productTotals.map((product) => (
                        <tr key={product.productKey} className="sales-product-row">
                            <td>{product.productDescription}</td>
                            <td className="align-right">{formatQuantity(product.totalQuantity)}</td>
                            <td className="align-right">{formatAmount(product.totalAmountInCents)}</td>
                        </tr>
                    ))}
                    <tr className="sales-grand-total-row">
                        <td>{t('productSales.grandTotal')}</td>
                        <td className="align-right">{formatQuantity(grandTotalQuantity)}</td>
                        <td className="align-right">{formatAmount(grandTotalAmountInCents)}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default ProductSalesReport;
